import { isEqual } from 'lodash-es';

import { resolveDriverPayRate } from '~hooks/useSiteRateSchedule/driverPayRate';
import {
  SiteRateAddOnAccount,
  SiteRateAddOnOpResult,
  SiteRateScheduleSaveResult,
} from '~hooks/useSiteRateSchedule/siteRateScheduleSaveResult';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';

import { cloneSiteRateSnapshot } from './siteRatesDirtyCheck';
import { SiteRateScheduleDTO } from './SiteRatesForm';

// TRE-14560: per-rate error bundle the form uses to render the inline
// error banner inside a FreightRateItem accordion. Keyed by `clientKey`
// in the parent map so the manager can look up errors per row even after
// the underlying form's `field.id`s churn through a `reset()`.
export interface FreightRateCardError {
  rateError?: string;
  // HTTP status from the failed SRS request when available. The card UI
  // shows the raw backend message only on 422 (user-actionable validation)
  // and falls back to a generic "failed to save — contact support" string
  // for everything else, since 5xx / network messages are not actionable.
  rateErrorStatus?: number;
  // Add-on errors keyed by account → form index. The manager uses these
  // to highlight individual add-on rows when we add per-add-on UI.
  addOnErrors: {
    customer: Record<number, string>;
    vendor: Record<number, string>;
    driver: Record<number, string>;
  };
}

export type FreightRateErrorMap = Map<string, FreightRateCardError>;

const emptyAddOnErrors = (): FreightRateCardError['addOnErrors'] => ({
  customer: {},
  vendor: {},
  driver: {},
});

const accountKey = (
  account: SiteRateAddOnAccount,
): keyof FreightRateCardError['addOnErrors'] =>
  account === 'driver' ? 'driver' : account === 'customer' ? 'customer' : 'vendor';

// Walks the per-add-on result list and returns the post-save state for one
// add-on slot — either a settled assignment (id stamped, isNew/isDeleted
// cleared) or `null` to remove the row (successful delete or never-saved
// new-then-deleted no-op).
const reconcileAddOn = (
  addOn: AddOnAssignmentSchema,
  result: SiteRateAddOnOpResult | undefined,
): AddOnAssignmentSchema | null => {
  if (!result) return addOn;

  if (result.error) {
    // Failed op leaves the row exactly as the user left it so the next
    // submit retries the same operation.
    return addOn;
  }

  if (result.op === 'delete') {
    return null;
  }

  if (result.op === 'create') {
    return {
      ...addOn,
      isNew: false,
      addOn: addOn.addOn
        ? { ...addOn.addOn, id: result.createdAssignmentId ?? addOn.addOn.id }
        : addOn.addOn,
    };
  }

  // Successful update — flags already cleared, but normalize for safety.
  return { ...addOn, isNew: false, isDeleted: false };
};

const applyAddOnResults = (
  current: AddOnAssignmentSchema[] | null | undefined,
  results: SiteRateAddOnOpResult[],
  account: SiteRateAddOnAccount,
): AddOnAssignmentSchema[] => {
  const list = current ?? [];
  const byIndex = new Map<number, SiteRateAddOnOpResult>();
  for (const r of results) {
    if (r.account === account) byIndex.set(r.index, r);
  }
  const next: AddOnAssignmentSchema[] = [];
  list.forEach((addOn, idx) => {
    const reconciled = reconcileAddOn(addOn, byIndex.get(idx));
    if (reconciled) next.push(reconciled);
  });
  return next;
};

const collectAddOnErrors = (
  results: SiteRateAddOnOpResult[],
): FreightRateCardError['addOnErrors'] => {
  const out = emptyAddOnErrors();
  for (const r of results) {
    if (!r.error) continue;
    out[accountKey(r.account)][r.index] = r.error;
  }
  return out;
};

const netRateBasis = (rate: SiteRateScheduleDTO, side: 'vendor' | 'driver') => {
  const payRate = side === 'vendor' ? rate.vendorRate : resolveDriverPayRate(rate);
  const percentage =
    side === 'vendor'
      ? rate.vendorPayCommissionPercentage
      : rate.driverPayCommissionPercentage;
  return [String(payRate?.rate ?? ''), payRate?.type?.value ?? null, percentage ?? null];
};

// A server net rate only holds for the gross, type and percentage it was computed from.
const keepMatchingNetRates = (
  rate: SiteRateScheduleDTO,
  savedRate: SiteRateScheduleDTO | undefined,
): SiteRateScheduleDTO => {
  const netRateMatches = (side: 'vendor' | 'driver') =>
    savedRate !== undefined &&
    isEqual(netRateBasis(rate, side), netRateBasis(savedRate, side));
  return {
    ...rate,
    netVendorRate: netRateMatches('vendor') ? rate.netVendorRate : null,
    netDriverRate: netRateMatches('driver') ? rate.netDriverRate : null,
  };
};

interface ApplySaveResultOutput {
  // Updated siteRates list to push back into the form. Server IDs are
  // stamped on successful creates; settled add-ons have their flags
  // cleared; successful deletes are removed; failed deletes stay in the
  // list with `isDeleted: true` so the next submit retries them.
  nextSiteRates: SiteRateScheduleDTO[];
  // Fresh snapshot map that matches `nextSiteRates`. The dirty check uses
  // this on the next submit so successfully-saved rate fields and add-ons
  // are no longer flagged as dirty (no duplicate writes), while failed
  // ones stay dirty and are retried.
  nextSnapshot: Map<string, SiteRateScheduleDTO>;
  errors: FreightRateErrorMap;
}

// TRE-14560: maps a `SiteRateScheduleSaveResult` onto the form's siteRates
// list and produces the next dirty-check snapshot. Pure — no I/O, no side
// effects — so it's straightforward to unit test.
//
// `priorSnapshot` is the snapshot the dirty check was using for THIS save
// (i.e. it represents what was actually on the server before the user's
// edits). It must be passed in so that failed updates preserve their prior
// entries: rebuilding the snapshot wholesale from `nextSiteRates` would
// treat the user's failed-to-save edits as if they had landed, which then
// drops them from the sparse PATCH body on the next submit.
export const applySiteRateSaveResult = (
  currentSiteRates: SiteRateScheduleDTO[],
  result: SiteRateScheduleSaveResult,
  priorSnapshot: Map<string, SiteRateScheduleDTO>,
): ApplySaveResultOutput => {
  const errors: FreightRateErrorMap = new Map();
  // Start from the prior snapshot so failed updates and failed deletes
  // retain their server-truth values. Successful ops overwrite or remove
  // their entries below.
  const nextSnapshot = new Map<string, SiteRateScheduleDTO>();
  for (const [id, snap] of priorSnapshot) {
    nextSnapshot.set(id, cloneSiteRateSnapshot(snap));
  }

  // Index per-op results by clientKey so we can fan out matches in a
  // single pass over `currentSiteRates`.
  const createsByKey = new Map<string, (typeof result.creates)[number]>();
  for (const c of result.creates) createsByKey.set(c.clientKey, c);
  const updatesByKey = new Map<string, (typeof result.updates)[number]>();
  for (const u of result.updates) updatesByKey.set(u.clientKey, u);
  const deletesByKey = new Map<string, (typeof result.deletes)[number]>();
  for (const d of result.deletes) deletesByKey.set(d.clientKey, d);

  const nextSiteRates: SiteRateScheduleDTO[] = [];

  for (const rate of currentSiteRates) {
    const clientKey = (rate.clientKey as string | undefined) ?? '';

    const createOp = clientKey ? createsByKey.get(clientKey) : undefined;
    const updateOp = clientKey ? updatesByKey.get(clientKey) : undefined;
    const deleteOp = clientKey ? deletesByKey.get(clientKey) : undefined;

    if (deleteOp) {
      if (deleteOp.rateError) {
        // Failed delete: keep the row visible & marked-for-delete so the
        // user knows it didn't actually delete and the next submit will
        // retry. The snapshot entry stays from priorSnapshot — the rate
        // still exists on the server.
        nextSiteRates.push(rate);
        errors.set(clientKey, {
          rateError: deleteOp.rateError,
          rateErrorStatus: deleteOp.rateErrorStatus,
          addOnErrors: emptyAddOnErrors(),
        });
      } else if (deleteOp.rateId) {
        // Successful delete — drop the row and forget the snapshot entry.
        nextSnapshot.delete(deleteOp.rateId);
      }
      continue;
    }

    if (createOp) {
      const succeededRate = !createOp.rateError && Boolean(createOp.serverId);
      const updatedRate: SiteRateScheduleDTO = succeededRate
        ? {
            ...keepMatchingNetRates(rate, undefined),
            id: createOp.serverId,
            customerAddOns: applyAddOnResults(
              rate.customerAddOns,
              createOp.addOns,
              'customer',
            ),
            vendorAddOns: applyAddOnResults(rate.vendorAddOns, createOp.addOns, 'vendor'),
            driverAddOns: applyAddOnResults(rate.driverAddOns, createOp.addOns, 'driver'),
          }
        : keepMatchingNetRates(rate, undefined);
      nextSiteRates.push(updatedRate);

      // Stamp the snapshot for successful creates so the row immediately
      // behaves like a server-backed row on the next dirty check. Failed
      // creates still have no `id` and stay in `toCreate` next submit —
      // no snapshot entry to write.
      if (succeededRate && createOp.serverId) {
        nextSnapshot.set(createOp.serverId, cloneSiteRateSnapshot(updatedRate));
      }

      const hasError =
        Boolean(createOp.rateError) || createOp.addOns.some((a) => a.error);
      if (hasError) {
        errors.set(clientKey, {
          rateError: createOp.rateError,
          rateErrorStatus: createOp.rateErrorStatus,
          addOnErrors: collectAddOnErrors(createOp.addOns),
        });
      }
      continue;
    }

    if (updateOp) {
      const priorEntry = updateOp.rateId ? priorSnapshot.get(updateOp.rateId) : undefined;
      const updatedRate: SiteRateScheduleDTO = {
        ...keepMatchingNetRates(rate, priorEntry),
        customerAddOns: applyAddOnResults(
          rate.customerAddOns,
          updateOp.addOns,
          'customer',
        ),
        vendorAddOns: applyAddOnResults(rate.vendorAddOns, updateOp.addOns, 'vendor'),
        driverAddOns: applyAddOnResults(rate.driverAddOns, updateOp.addOns, 'driver'),
      };
      nextSiteRates.push(updatedRate);

      // Snapshot reconciliation for updates:
      //   - SRS-level PATCH succeeded (or wasn't attempted because the
      //     sparse body was empty): the rate-level fields the user typed
      //     are now what the server holds, so adopt `updatedRate` as the
      //     new snapshot. Add-on settled flags propagate through too.
      //   - SRS-level PATCH failed: the server still holds the *prior*
      //     rate-level values. Keep the prior snapshot's SRS-level fields
      //     so the user's pending edits remain dirty on the next submit
      //     and the retried PATCH carries them; but still pull add-on
      //     state from `updatedRate` because per-add-on successes /
      //     failures are tracked independently of the rate-level PATCH.
      if (updateOp.rateError && priorEntry) {
        nextSnapshot.set(updateOp.rateId, {
          ...priorEntry,
          id: updatedRate.id,
          clientKey: updatedRate.clientKey,
          customerAddOns: updatedRate.customerAddOns,
          vendorAddOns: updatedRate.vendorAddOns,
          driverAddOns: updatedRate.driverAddOns,
        });
      } else if (updateOp.rateId) {
        nextSnapshot.set(updateOp.rateId, cloneSiteRateSnapshot(updatedRate));
      }

      const hasError =
        Boolean(updateOp.rateError) || updateOp.addOns.some((a) => a.error);
      if (hasError) {
        errors.set(clientKey, {
          rateError: updateOp.rateError,
          rateErrorStatus: updateOp.rateErrorStatus,
          addOnErrors: collectAddOnErrors(updateOp.addOns),
        });
      }
      continue;
    }

    // Rate was not part of this save (clean / not dirty). Pass through —
    // and leave its snapshot entry as it was in priorSnapshot.
    nextSiteRates.push(rate);
  }

  return {
    nextSiteRates,
    nextSnapshot,
    errors,
  };
};
